import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import api, { BASE_URL } from "../../../Utils/api";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import pdfjsWorker from "pdfjs-dist/legacy/build/pdf.worker.entry";
import {
  FaSearchMinus,
  FaSearchPlus,
  FaRedo,
  FaEye,
  FaTrash,
} from "react-icons/fa";
import "./PdfViewerModal.css";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.25;

function buildFileUrl(filePath) {
  const normalized = String(filePath || "").replace(/^\/+/, "");
  return `${BASE_URL}/${normalized}`;
}

function resolveFetchUrl({ filePath, documentoId, aditivoId }) {
  if (documentoId) {
    return `${BASE_URL}/api/documentos/recuperaAnexoDocumento/${documentoId}`;
  }
  if (aditivoId) {
    return `${BASE_URL}/api/documentos/recuperaAnexoAditivo/${aditivoId}`;
  }
  if (filePath) {
    return buildFileUrl(filePath);
  }
  return null;
}

function usePdfViewerProtection(active) {
  useEffect(() => {
    if (!active) return undefined;

    const blockKeys = (event) => {
      const key = String(event.key || "").toLowerCase();
      if (key === "printscreen") {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
      if ((event.ctrlKey || event.metaKey) && ["s", "p", "u", "a", "c"].includes(key)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const blockContextMenu = (event) => {
      event.preventDefault();
    };

    const blockClipboard = (event) => {
      event.preventDefault();
    };

    window.addEventListener("keydown", blockKeys, true);
    window.addEventListener("contextmenu", blockContextMenu, true);
    document.addEventListener("copy", blockClipboard, true);
    document.addEventListener("cut", blockClipboard, true);

    return () => {
      window.removeEventListener("keydown", blockKeys, true);
      window.removeEventListener("contextmenu", blockContextMenu, true);
      document.removeEventListener("copy", blockClipboard, true);
      document.removeEventListener("cut", blockClipboard, true);
    };
  }, [active]);
}

const PdfViewerModal = ({
  show,
  onHide,
  filePath,
  documentoId,
  aditivoId,
  title = "Visualizar PDF",
  onExcluir,
}) => {
  const pagesContainerRef = useRef(null);
  const pdfDocRef = useRef(null);
  const renderTaskRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [contentHidden, setContentHidden] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [erroExclusao, setErroExclusao] = useState(null);

  const hasSource = Boolean(filePath || documentoId || aditivoId);

  usePdfViewerProtection(show);

  useEffect(() => {
    if (!show) return undefined;

    const onBlur = () => setContentHidden(true);
    const onFocus = () => setContentHidden(false);
    const onVisibilityChange = () => {
      setContentHidden(document.visibilityState !== "visible");
    };

    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [show]);

  const cancelRenderTask = useCallback(() => {
    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
      renderTaskRef.current = null;
    }
  }, []);

  const renderPages = useCallback(async (pdfDoc, nextScale, nextRotation) => {
    const container = pagesContainerRef.current;
    if (!container || !pdfDoc) return;

    cancelRenderTask();
    container.innerHTML = "";

    for (let pageNumber = 1; pageNumber <= pdfDoc.numPages; pageNumber += 1) {
      const page = await pdfDoc.getPage(pageNumber);
      const viewport = page.getViewport({ scale: nextScale, rotation: nextRotation });
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.className = "pdf-viewer-page-canvas";
      canvas.setAttribute("draggable", "false");

      const wrapper = document.createElement("div");
      wrapper.className = "pdf-viewer-page";
      wrapper.appendChild(canvas);
      container.appendChild(wrapper);

      const renderTask = page.render({ canvasContext: context, viewport });
      renderTaskRef.current = renderTask;
      await renderTask.promise;
      renderTaskRef.current = null;
    }
  }, [cancelRenderTask]);

  const fetchPdfDocument = useCallback(async (source) => {
    const url = resolveFetchUrl(source);
    if (!url) {
      throw new Error("Arquivo não informado.");
    }
    const token = localStorage.getItem("authToken");
    const { data } = await api.get(url, {
      responseType: "arraybuffer",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const loadingTask = pdfjsLib.getDocument({ data });
    return loadingTask.promise;
  }, []);

  const loadPdf = useCallback(async (source, initialScale = 1, initialRotation = 0) => {
    if (!source.filePath && !source.documentoId && !source.aditivoId) {
      setError("Arquivo não informado.");
      return;
    }

    setLoading(true);
    setError(null);
    setNumPages(0);
    cancelRenderTask();

    if (pdfDocRef.current) {
      pdfDocRef.current.destroy();
      pdfDocRef.current = null;
    }

    if (pagesContainerRef.current) {
      pagesContainerRef.current.innerHTML = "";
    }

    try {
      const pdfDoc = await fetchPdfDocument(source);
      pdfDocRef.current = pdfDoc;
      setNumPages(pdfDoc.numPages);
      await renderPages(pdfDoc, initialScale, initialRotation);
    } catch (err) {
      console.error("Erro ao carregar PDF:", err);
      let apiMessage = null;
      if (err?.response?.data instanceof ArrayBuffer) {
        try {
          const text = new TextDecoder().decode(err.response.data);
          const parsed = JSON.parse(text);
          apiMessage = parsed.message;
        } catch (_) {
          apiMessage = null;
        }
      } else if (err?.response?.data?.message) {
        apiMessage = err.response.data.message;
      }
      setError(apiMessage || err?.message || "Não foi possível carregar o PDF.");
    } finally {
      setLoading(false);
    }
  }, [cancelRenderTask, renderPages, fetchPdfDocument]);

  useEffect(() => {
    if (!show || !hasSource) return undefined;

    const source = { filePath, documentoId, aditivoId };
    setScale(1);
    setRotation(0);
    setContentHidden(false);
    loadPdf(source, 1, 0);

    return () => {
      cancelRenderTask();
      if (pdfDocRef.current) {
        pdfDocRef.current.destroy();
        pdfDocRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, filePath, documentoId, aditivoId]);

  useEffect(() => {
    if (!show || !pdfDocRef.current || loading) return undefined;
    renderPages(pdfDocRef.current, scale, rotation).catch((err) => {
      if (err?.name !== "RenderingCancelledException") {
        console.error("Erro ao renderizar PDF:", err);
      }
    });
  }, [scale, rotation, show, loading, renderPages]);

  const handleZoomIn = () => {
    setScale((prev) => Math.min(ZOOM_MAX, Number((prev + ZOOM_STEP).toFixed(2))));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(ZOOM_MIN, Number((prev - ZOOM_STEP).toFixed(2))));
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleClose = () => {
    cancelRenderTask();
    if (pdfDocRef.current) {
      pdfDocRef.current.destroy();
      pdfDocRef.current = null;
    }
    setError(null);
    setNumPages(0);
    setConfirmando(false);
    setErroExclusao(null);
    onHide();
  };

  const handleExcluirClick = () => {
    setErroExclusao(null);
    setConfirmando(true);
  };

  const handleCancelarConfirmacao = () => setConfirmando(false);

  const handleConfirmarExclusao = async () => {
    setExcluindo(true);
    setErroExclusao(null);
    try {
      await onExcluir();
    } catch (err) {
      setErroExclusao(err?.response?.data?.message || err?.message || "Falha ao remover o anexo.");
      setConfirmando(false);
    } finally {
      setExcluindo(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
      keyboard={false}
      dialogClassName="pdf-viewer-modal-dialog"
      contentClassName="pdf-viewer-modal-content"
      className="pdf-viewer-modal"
    >
      <Modal.Header closeButton className="pdf-viewer-modal-header">
        <Modal.Title className="pdf-viewer-modal-title">{title}</Modal.Title>
      </Modal.Header>

      <div className="pdf-viewer-toolbar">
        <div className="pdf-viewer-toolbar-group">
          <Button variant="light" size="sm" onClick={handleZoomOut} disabled={loading || scale <= ZOOM_MIN} title="Diminuir zoom">
            <FaSearchMinus />
          </Button>
          <span className="pdf-viewer-zoom-label">{Math.round(scale * 100)}%</span>
          <Button variant="light" size="sm" onClick={handleZoomIn} disabled={loading || scale >= ZOOM_MAX} title="Aumentar zoom">
            <FaSearchPlus />
          </Button>
          <Button variant="light" size="sm" onClick={handleRotate} disabled={loading} title="Rotacionar 90°">
            <FaRedo />
          </Button>
        </div>
        {numPages > 0 && (
          <span className="pdf-viewer-pages-label">
            {numPages} {numPages === 1 ? "página" : "páginas"}
          </span>
        )}
      </div>

      <Modal.Body className="pdf-viewer-modal-body">
        {loading && (
          <div className="pdf-viewer-loading">
            <Spinner animation="border" role="status" />
            <span>Carregando documento...</span>
          </div>
        )}

        {!loading && error && (
          <div className="pdf-viewer-error alert alert-danger mb-0" role="alert">
            {error}
          </div>
        )}

        {!loading && !error && (
          <div className={`pdf-viewer-scroll ${contentHidden ? "pdf-viewer-scroll--hidden" : ""}`}>
            <div ref={pagesContainerRef} className="pdf-viewer-pages" />
            {contentHidden && (
              <div className="pdf-viewer-privacy-overlay">
                <FaEye size={28} />
                <span>Conteúdo oculto por segurança</span>
              </div>
            )}
          </div>
        )}
      </Modal.Body>

      {onExcluir && (
        <Modal.Footer className="pdf-viewer-modal-footer">
          {erroExclusao && (
            <span className="text-danger me-auto" style={{ fontSize: "0.85rem" }}>
              {erroExclusao}
            </span>
          )}
          {!confirmando ? (
            <Button
              variant="outline-danger"
              size="sm"
              onClick={handleExcluirClick}
              disabled={loading || excluindo}
            >
              <FaTrash className="me-1" />
              Excluir anexo
            </Button>
          ) : (
            <>
              <span className="me-auto" style={{ fontSize: "0.875rem" }}>
                Remover este PDF?
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleCancelarConfirmacao}
                disabled={excluindo}
              >
                Não
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleConfirmarExclusao}
                disabled={excluindo}
              >
                {excluindo ? <Spinner animation="border" size="sm" /> : "Sim, remover"}
              </Button>
            </>
          )}
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default PdfViewerModal;
